import { useState } from 'react';
import { Alert, Button, Group, Text } from '@mantine/core';
import { api, errorMessage } from '../api/client';
import { useAuth } from '../auth/AuthContext';

/**
 * A quiet reminder to confirm the email address on the account.
 *
 * It never blocks anything: everything in the dashboard still works unconfirmed, so this is a
 * line at the top of the page that can be hidden for the rest of the visit.
 */

const DISMISSED_KEY = 'quizee.verify_banner_hidden';

function wasDismissed(): boolean {
  try {
    return window.sessionStorage.getItem(DISMISSED_KEY) === 'yes';
  } catch {
    return false;
  }
}

function rememberDismissed(): void {
  try {
    window.sessionStorage.setItem(DISMISSED_KEY, 'yes');
  } catch {
    // Storage can be blocked. The banner then comes back on the next page load, which is no harm.
  }
}

export function VerifyEmailBanner() {
  const { user } = useAuth();
  const [hidden, setHidden] = useState(wasDismissed);
  const [sent, setSent] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (user === null || user.email_verified !== false || hidden) return null;

  async function send() {
    setProblem(null);
    setBusy(true);
    try {
      await api.requestEmailVerification();
      setSent(true);
    } catch (error) {
      setProblem(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  function hide() {
    rememberDismissed();
    setHidden(true);
  }

  return (
    <Alert
      className="verification-notice"
      role="status"
      color="gray"
      variant="light"
      radius="md"
      mb="lg"
    >
      <Group justify="space-between" align="center" gap="sm">
        <Text size="sm" style={{ flex: '1 1 18rem' }}>
          <strong>Confirm your email address.</strong> We sent a link to {user.email} for password
          resets and account notices.
        </Text>
        <Group gap="md" align="center">
          {sent ? (
            <Text size="sm" c="violet">
              Sent. Check your inbox.
            </Text>
          ) : (
            <Button
              variant="subtle"
              color="gray"
              size="compact-sm"
              disabled={busy}
              onClick={() => void send()}
            >
              {busy ? 'Sending...' : 'Send it again'}
            </Button>
          )}
          <Button variant="subtle" color="gray" size="compact-sm" onClick={hide}>
            Hide for now
          </Button>
        </Group>
      </Group>
      {problem !== null && (
        <Text size="sm" c="dimmed" mt="xs">
          {problem}
        </Text>
      )}
    </Alert>
  );
}
